import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.tsx'

function GrayButton({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
	return (
		<Button
			variant="outline"
			{...props}
			className={cn(
				'relative h-9 w-9 p-0',
				'overflow-hidden rounded-lg',
				'bg-gradient-to-b from-zinc-50 to-zinc-100',
				'dark:from-zinc-800 dark:to-zinc-900',
				'border border-zinc-200 dark:border-zinc-800',
				'hover:hover:border-zinc-300 dark:hover:hover:border-zinc-700',
				'transition-all duration-300 ease-out',
				'group',
				className,
			)}
		>
			<span
				className={cn(
					'absolute inset-0',
					'bg-gradient-to-r from-indigo-500/0 via-indigo-500/10 to-indigo-500/0',
					'translate-x-[-100%]',
					'group-hover:hover:translate-x-[100%]',
					'transition-transform duration-500',
					'ease-out',
				)}
			/>
			{props.children}
		</Button>
	)
}

const buttonVariants = cva(
	'inline-flex items-center justify-center rounded-xl text-sm font-medium ring-offset-background outline-none transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out focus-visible:ring-1 ring-offset-1 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				outline: 'text-primary border bg-neutral-50/20 border-gray-200 hover:bg-neutral-100/50 text-neutral-700',
        success: 'bg-emerald-600 text-white hover:bg-emerald-700',
				secondary: 'bg-neutral-100 border-none ring-none text-secondary-foreground hover:bg-neutral-200',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary hover:bg-neutral-200/40',
			},
			size: {
				default: 'h-10 px-4 py-2',
				wide: 'px-24 py-5',
				sm: 'h-8 px-3 text-xs',
				lg: 'h-11 px-8',
				pill: 'px-12 py-3 leading-3',
				icon: 'h-10 w-10',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
	tooltip?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, tooltip, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button'
		const button = <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />

		if (tooltip) {
			return (
				<TooltipProvider delayDuration={100}>
					<Tooltip>
						<TooltipTrigger asChild className={className}>
							{button}
						</TooltipTrigger>
						<TooltipContent>
							<p className="text-xs font-light text-neutral-500">{tooltip}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			)
		}

		return button
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants, GrayButton }
